import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LanguageTarget } from '../api/types';
import { FieldLabel } from './FieldLabel';
import {
  LanguageTargetsEditor,
  MultiLanguageSwitch,
  defaultTargetUrl,
  languageTargetsError,
  normalizedLanguageTargets,
  startLanguages,
} from './LanguageTargetsEditor';
import {
  ActiveToggle,
  FormActions,
  GroupField,
  LockedValue,
  ValidationBanner,
  isHttpUrl,
  type CodeFormProps,
} from './qrCodeFormParts';

/** A code pointing at an address somebody typed. Its destination can be repointed at any time. */
export function DestinationCodeForm({
  groups,
  qrCode,
  submitting = false,
  onSubmit,
  onCancel,
  kindSwitch,
}: CodeFormProps) {
  const { t } = useTranslation();
  const isEditing = qrCode !== undefined;
  const editableGroups = groups.filter((group) => group.canEditQrCodes);

  const [ownerGroupId, setOwnerGroupId] = useState(qrCode?.ownerGroupId ?? editableGroups[0]?.id ?? '');
  const [title, setTitle] = useState(qrCode?.title ?? '');
  const [targetUrl, setTargetUrl] = useState(qrCode?.targetUrl ?? '');
  const [languages, setLanguages] = useState<LanguageTarget[]>(qrCode?.languageTargets ?? []);
  const multiLanguage = languages.length > 0;

  function toggleLanguages(on: boolean) {
    if (on) {
      setLanguages(startLanguages(targetUrl.trim()));
      return;
    }

    setTargetUrl(defaultTargetUrl(languages));
    setLanguages([]);
  }
  const [slug, setSlug] = useState('');
  const [isActive, setIsActive] = useState(qrCode?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!isEditing && !ownerGroupId) {
      setValidationError(t('qrCode.validation.groupRequired'));
      return;
    }

    if (!title.trim()) {
      setValidationError(t('qrCode.validation.titleRequired'));
      return;
    }

    const languagesError = multiLanguage ? languageTargetsError(languages, t) : null;
    if (languagesError) {
      setValidationError(languagesError);
      return;
    }

    if (!multiLanguage && !isHttpUrl(targetUrl.trim())) {
      setValidationError(t('qrCode.validation.urlRequired'));
      return;
    }

    setValidationError(null);
    const languageTargets = multiLanguage ? normalizedLanguageTargets(languages) : [];

    await onSubmit({
      ownerGroupId,
      title: title.trim(),
      kind: 'Destination',
      targetUrl: multiLanguage ? defaultTargetUrl(languageTargets) : targetUrl.trim(),
      languageTargets,
      product: undefined,
      slug: slug.trim() === '' ? undefined : slug.trim(),
      isActive,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-label={isEditing ? t('qrCode.formLabelEdit') : t('qrCode.formLabelCreate')}
    >
      <ValidationBanner message={validationError} />

      <GroupField
        groups={editableGroups}
        qrCode={qrCode}
        value={ownerGroupId}
        disabled={submitting}
        onChange={setOwnerGroupId}
        kindSwitch={kindSwitch}
      />

      <div className="field">
        <FieldLabel htmlFor="qr-title" icon="pi-tag" required>
          {t('qrCode.title')}
        </FieldLabel>
        <input
          id="qr-title"
          type="text"
          className="form-control"
          aria-required="true"
          value={title}
          disabled={submitting}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div className="field">
        <FieldLabel htmlFor={multiLanguage ? 'qr-languages' : 'qr-target'} icon="pi-link" required>
          {t('qrCode.destinationUrl')}
        </FieldLabel>
        <div className="destination-row">
          <div className="destination-row__value">
            {multiLanguage ? (
              <LanguageTargetsEditor id="qr-languages" value={languages} onChange={setLanguages} disabled={submitting} />
            ) : (
              <input
                id="qr-target"
                type="url"
                className="form-control"
                aria-required="true"
                value={targetUrl}
                placeholder="https://example.com/landing-page"
                disabled={submitting}
                onChange={(event) => setTargetUrl(event.target.value)}
              />
            )}
          </div>
          <MultiLanguageSwitch checked={multiLanguage} disabled={submitting} onChange={toggleLanguages} />
        </div>
        {!multiLanguage && (
          <span className="hint">
            {isEditing ? t('qrCode.destinationHintEdit') : t('qrCode.destinationHintNew')}
          </span>
        )}
      </div>

      {isEditing ? (
        <>
          <LockedValue
            label={t('qrCode.linkId')}
            icon="pi-hashtag"
            value={qrCode.slug}
            mono
            copyable
            hint={t('qrCode.linkIdLockedHint')}
          />
          <ActiveToggle checked={isActive} disabled={submitting} onChange={setIsActive} />
        </>
      ) : (
        <div className="field">
          <FieldLabel htmlFor="qr-slug" icon="pi-hashtag">
            {t('qrCode.linkIdCustom')}
          </FieldLabel>
          <input
            id="qr-slug"
            type="text"
            className="form-control"
            value={slug}
            placeholder={t('qrCode.linkIdPlaceholder')}
            disabled={submitting}
            onChange={(event) => setSlug(event.target.value)}
          />
          <span className="hint">
            {t('qrCode.linkIdHint')} {t('qrCode.linkIdLockedHint')}
          </span>
        </div>
      )}

      <FormActions submitting={submitting} isEditing={isEditing} onCancel={onCancel} />
    </form>
  );
}
